// ─────────────────────────────────────────────
//  qgendaIdentity — read the department's OWN identity map instead of guessing.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  WHAT THIS TAB IS. The AHP cells on `Assignment Sheet` are NOT typed from
//  scratch: QGenda exports `Last, First` plus a shift-block name, a formula
//  renders "Toni Abraham 3", and humans edit on top of that. The `Qgenda
//  Report` tab of the SAME daily workbook is the generator's own input, so it
//  carries — free, republished every morning — the two things the importer has
//  been inventing regexes for:
//
//    Compton, Kang   →  "Chip Compton 3"     the display name the sheet prints
//    Huang, Jessica  →  "HUA"                the MD short code
//
//  ⚠️ `Chip` ↔ `Kang` is why a generic nickname table can never work, and why
//  we do not need one. No dictionary derives "Chip" from "Kang"; this tab
//  states it. Everything here exists to read that statement, not to infer it.
//
//  ⚠️ THE TAB IS HAND-EDITED AND RESTRUCTURED, SO NOTHING IS READ BY POSITION.
//  Measured on the live file, 2026-08-20: it holds TWO stacked reports — the
//  current print AND a stale February one — whose columns are in DIFFERENT
//  places (identity at col 4 in one, col 9 in the other) and whose identity
//  cells are in DIFFERENT registers (`Abraham, Toni` vs the compact key
//  `CamilleF`); cells carry a leading `x`/`X` marker, inconsistently, on either
//  half of a pair; the generator welds a campus tag onto the surname
//  (`WCDickinson, Keely`, `TAMPACompton, Kang`); and the trailing "Schedule
//  Notes:" block is prose that names real people.
//  So the columns are DISCOVERED from the data: a [block header, identity,
//  display] triple is found by asking which adjacent column pair repeatedly
//  names the same person on the same row. A moved column, an inserted header
//  row, or a third stacked report changes nothing.
//
//  ⚠️ NEVER THROWS, AND NEVER PARTIALLY LIES. A missing tab, a renamed header,
//  a restructured sheet → `{ ok: false, reason, people: [] }`, and the caller
//  carries on with the rungs below this one: an empty map costs resolution
//  rate, an invented map costs a patient the right anaesthetist. Likewise a
//  display name covering two DIFFERENT people is not an answer — it lands in
//  `conflicts` and stays OUT of the index, the same refusal the timeline's name
//  menu makes on a duplicated name.
//
//  Pure — no Firebase, no React, no network. The caller fetches the tab and
//  hands over a 2D array of strings.
// ─────────────────────────────────────────────

import { normalizeName } from "../team-directory/normalizeName";
import { parseTrainingRoster, trainingDisplayEntries } from "./qgendaTraining";

export const QGENDA_TAB_TITLE = "Qgenda Report";

const letters = (s) => String(s || "").toLowerCase().replace(/[^a-z]/g, "");
const cellAt = (row, i) => String(row?.[i] ?? "").trim();

// Leading status marker. Stripped ONLY when the next character is an uppercase
// letter, so a real "Xavier" (X + lowercase) is never mutilated. Observed as
// both `xWright, James` and `XRiveraP`.
const stripMarker = (s) => String(s || "").replace(/^[xX](?=[A-Z])/, "").trim();

// A campus tag glued onto a Capitalized surname: `TAMPACompton`, `WCDickinson`.
// The discriminator is TWO OR MORE leading capitals followed by a Capitalized
// word — which no real surname in this roster has (`McKay`, `DiBartolomeo`,
// `MacMullan`, `O'Connell` all carry exactly one leading capital). Deliberately
// NOT a list of campus prefixes: a list would have to grow with the tenant, and
// stripping "CAR" off "Cardenas" is exactly the kind of quiet damage this file
// exists to avoid.
const GLUED_TAG_RE = /^[A-Z]{2,}(?=[A-Z][a-z])/;
const unglue = (s) => String(s || "").replace(GLUED_TAG_RE, "");

// A trailing clock window on an identity cell: `Villanueva, Carlos 11:00-23:00`.
const TRAILING_WINDOW_RE = /\s+\d{1,2}:\d{2}\s*-\s*\d{1,2}:\d{2}\s*$/;

// An MD short code cell: `HUA`, `WIL (1)`, `MAR (7)`.
const SHORT_CODE_RE = /^([A-Z]{2,5})(?:\s*\((\d{1,2})\))?$/;

// A block-header cell: `AT-APP-7a-3p, (14 M-Th/10 F)`, `WC-APP 24hr-7a-7a OB`,
// `AT-APP-Day Hawk-2 11:00-23:00`, `AT-APP-MISC. Shift`.
const BLOCK_HEADER_RE = /(^|[^A-Za-z])(APP|PHY)([^A-Za-z]|$)/;

// A report starts here (row 0 is always a block start too, marker or not).
const REPORT_START_RE = /envision\s+anesthesia/i;
const PRINTED_RE = /printed:\s*([0-9]{1,2}\/[0-9]{1,2}\/[0-9]{2,4})/i;
const NOTES_MARKER_RE = /^schedule\s+notes:/i; // below it: prose naming real people

/**
 * Do these two cells name the same person? `Abraham, Toni` ↔ `Toni Abraham 3`
 * share a whole token; `CamilleF` ↔ `Felix Camille 3` and `Mercado-RiveraJ` ↔
 * `Jean Mercado 5` need the compact key's PREFIX form. The 4-character floor on
 * that form is what stops short surnames (`Sa`) from binding unrelated columns.
 */
function namesAgree(idCell, displayCell) {
  const a = stripMarker(idCell).split(/[\s,]+/).map(letters).filter((t) => t.length > 1);
  const b = stripMarker(displayCell).split(/[\s,]+/).map(letters).filter((t) => t.length > 1);
  for (const x of a) {
    for (const y of b) {
      if (x === y) return true;
      if (x.startsWith(y) && y.length >= 4) return true;
      if (y.startsWith(x) && x.length >= 4) return true;
    }
  }
  return false;
}

/** Split the grid into stacked reports, each `{ start, end, printedAt }`. */
function findBlocks(values) {
  const starts = [];
  values.forEach((row, r) => {
    const first = cellAt(row, 0);
    if (r === 0 || REPORT_START_RE.test(first)) starts.push(r);
  });
  if (!starts.length) starts.push(0);
  return starts.map((start, i) => {
    const end = i + 1 < starts.length ? starts[i + 1] : values.length;
    let printedAt = null;
    for (let r = start; r < Math.min(end, start + 4); r += 1) {
      const m = PRINTED_RE.exec(cellAt(values[r], 0));
      if (m) { printedAt = m[1]; break; }
    }
    return { start, end, printedAt };
  });
}

/** Rows of a block that are real grid rows (above the prose "Schedule Notes"). */
function blockLimit(values, block) {
  for (let r = block.start; r < block.end; r += 1) {
    if (NOTES_MARKER_RE.test(cellAt(values[r], 0))) return r;
  }
  return block.end;
}

/**
 * Find the [identity, display] and [identity, shortCode] column pairs inside
 * one block by SCORING every adjacent pair. A pair needs at least two agreeing
 * rows, which no accidental neighbouring of a header and a name has ever
 * produced on this tab.
 */
function findColumnPairs(values, from, to) {
  let width = 0;
  for (let r = from; r < to; r += 1) width = Math.max(width, values[r]?.length || 0);

  const display = [];
  const codes = [];
  for (let i = 0; i + 1 < width; i += 1) {
    let nameHits = 0;
    let codeHits = 0;
    for (let r = from; r < to; r += 1) {
      const left = cellAt(values[r], i);
      const right = cellAt(values[r], i + 1);
      if (!left || !right) continue;
      if (BLOCK_HEADER_RE.test(left)) continue; // a header is never an identity
      if (SHORT_CODE_RE.test(stripMarker(right))) codeHits += 1;
      else if (namesAgree(left, right)) nameHits += 1;
    }
    if (nameHits >= 2) display.push({ idCol: i, valueCol: i + 1, hits: nameHits });
    if (codeHits >= 2) codes.push({ idCol: i, valueCol: i + 1, hits: codeHits });
  }
  // A column may not be both the identity of one pair and the value of another.
  const claimed = new Set();
  const keep = (pairs) => pairs
    .slice()
    .sort((a, b) => b.hits - a.hits)
    .filter((p) => {
      if (claimed.has(p.idCol) || claimed.has(p.valueCol)) return false;
      claimed.add(p.idCol);
      claimed.add(p.valueCol);
      return true;
    })
    .sort((a, b) => a.idCol - b.idCol);
  return { display: keep(display), codes: keep(codes) };
}

/** Is this column, just left of an identity column, the block-header column? */
function headerColumnFor(values, from, to, idCol) {
  const col = idCol - 1;
  if (col < 0) return -1;
  for (let r = from; r < to; r += 1) {
    if (BLOCK_HEADER_RE.test(cellAt(values[r], col))) return col;
  }
  return -1;
}

/** `Last, First` or a compact `SurnameI` key → a legal-name record. */
function parseIdentityCell(raw) {
  const clean = stripMarker(raw).replace(TRAILING_WINDOW_RE, "").trim();
  if (!clean) return null;
  const comma = clean.indexOf(",");
  if (comma >= 0) {
    const last = unglue(clean.slice(0, comma).trim());
    const first = clean.slice(comma + 1).trim();
    if (!last) return null;
    return { legalLast: last, legalFirst: first, legalName: `${first} ${last}`.trim(), compact: false };
  }
  // Compact QGenda key: surname with the first initial glued on (`CamilleF`,
  // `Alicea FerrerM`, `JohnsonJ2`). The initial is recoverable; the given name
  // is not — the display half of the pair is what carries it.
  const m = /^(.*?[a-z])([A-Z])\d*$/.exec(unglue(clean));
  if (m) return { legalLast: m[1].trim(), legalFirst: "", legalName: m[1].trim(), compact: true, initial: m[2] };
  return { legalLast: unglue(clean), legalFirst: "", legalName: unglue(clean), compact: true };
}

/**
 * Split a rendered cell into the printed name and the printed shift token.
 * Anchored on the SURNAME we already know from the identity half — which is
 * what makes `Sarabel Mercado Torres 3` (one three-word human) and
 * `Femi John 7p-7a OB` come out right where a trailing-token rule cannot.
 */
function splitDisplayCell(raw, legalLast) {
  const clean = stripMarker(raw);
  const tokens = clean.split(/\s+/).filter(Boolean);
  if (!tokens.length) return { displayName: "", shiftToken: "" };
  const surnameTokens = String(legalLast || "").split(/\s+/).map(letters).filter(Boolean);
  const tail = surnameTokens[surnameTokens.length - 1];
  if (tail) {
    for (let i = tokens.length - 1; i >= 0; i -= 1) {
      if (letters(tokens[i]) === tail) {
        return {
          displayName: tokens.slice(0, i + 1).join(" "),
          shiftToken: tokens.slice(i + 1).join(" "),
        };
      }
    }
  }
  // The two halves disagree on spelling (`Hofmann, Reed` / `Reed Hoffman 5`).
  // Fall back to peeling tokens that cannot be name words.
  const out = [...tokens];
  while (out.length > 1 && /[\d@]/.test(out[out.length - 1])) out.pop();
  return { displayName: out.join(" "), shiftToken: tokens.slice(out.length).join(" ") };
}

/**
 * Recover the given name a COMPACT identity key does not carry.
 *
 * `xCamilleF` states the surname and the initial and nothing else; the display
 * half of the same pair states `Felix Camille 3`. Taking "Felix" is safe ONLY
 * because the key's own initial confirms it — if the display leads with a
 * familiar form the key does not endorse (`xSchneiderR` beside `Bob Schneider`,
 * R ≠ B) the record stays surname-only, which is honest and still useful: it
 * cannot contradict the fuller record the current report carries.
 *
 * ⚠️ THE RESULT IS FLAGGED `weakFirst`, AND THAT FLAG IS LOAD-BEARING. All the
 * key really asserts is "surname + initial C"; the given name came from the
 * PRINTED half, which is the very thing that may be a nickname. `xSamuelsC`
 * beside `Cassie Samuels 5` is the same human as the current report's
 * `Samuels, Cassandra` — treating "Cassie" as an asserted legal first name made
 * her look like two people and dropped her from the map, along with Dimitrina
 * Borissova and Kimberly Tavares: two of the very phantom-duplicate cases this
 * tab is being read to fix.
 */
function withGivenName(id, displayName) {
  if (!id.compact || id.legalFirst || !id.initial) return id;
  const first = String(displayName).split(/\s+/)[0] || "";
  if (letters(first).charAt(0) !== letters(id.initial)) return id;
  return { ...id, legalFirst: first, weakFirst: true, legalName: `${first} ${id.legalLast}`.trim() };
}

/**
 * Parse the `Qgenda Report` tab.
 *
 * @param {Array<Array<string>>} values raw 2D tab values (caller fetches)
 * @returns {{ ok, reason, source,
 *   people: Array<{legalLast, legalFirst, legalName, weakFirst, displayName,
 *                  displayRaw, shiftToken, shiftBlock, row, block}>,
 *   trainees: Array<{legalLast, legalFirst, legalName, abbreviated, note, row}>,
 *   codes:  Array<{legalLast, legalFirst, legalName, shortCode, callSlot, row, block}>,
 *   conflicts: Array<{displayName, legalNames: string[]}>,
 *   blocks: Array<{start, end, printedAt}> }}
 */
export function parseQgendaIdentity(values) {
  const empty = { ok: false, reason: "", source: QGENDA_TAB_TITLE, people: [], codes: [], conflicts: [], blocks: [] };
  if (!Array.isArray(values) || !values.length) return { ...empty, reason: "tab absent or empty" };

  const people = [];
  const codes = [];
  let blocks = [];
  let trainees = [];
  try {
    // The trainee roster below "Schedule Notes:" — the only place the workbook
    // spells out the surnames the Assignment Sheet abbreviates to one letter.
    // Read by its own strict line shape (qgendaTraining), never by the column
    // scan, which stops above it because that region is prose.
    trainees = parseTrainingRoster(values);
    people.push(...trainingDisplayEntries(trainees));
    blocks = findBlocks(values);
    blocks.forEach((block, bi) => {
      const to = blockLimit(values, block);
      const { display, codes: codePairs } = findColumnPairs(values, block.start, to);

      display.forEach(({ idCol, valueCol }) => {
        const headerCol = headerColumnFor(values, block.start, to, idCol);
        let shiftBlock = "";
        for (let r = block.start; r < to; r += 1) {
          if (headerCol >= 0) {
            const h = cellAt(values[r], headerCol);
            if (h && BLOCK_HEADER_RE.test(h)) shiftBlock = h;
          }
          const id = parseIdentityCell(cellAt(values[r], idCol));
          const displayRaw = stripMarker(cellAt(values[r], valueCol));
          if (!id || !displayRaw) continue;
          const { displayName, shiftToken } = splitDisplayCell(displayRaw, id.legalLast);
          if (!displayName) continue;
          const named = withGivenName(id, displayName);
          people.push({
            legalLast: named.legalLast,
            legalFirst: named.legalFirst,
            legalName: named.legalName,
            weakFirst: named.weakFirst === true,
            displayName,
            displayRaw,
            shiftToken,
            shiftBlock,
            row: r,
            block: bi,
          });
        }
      });

      codePairs.forEach(({ idCol, valueCol }) => {
        for (let r = block.start; r < to; r += 1) {
          const id = parseIdentityCell(cellAt(values[r], idCol));
          const m = SHORT_CODE_RE.exec(stripMarker(cellAt(values[r], valueCol)));
          if (!id || !m) continue;
          codes.push({
            legalLast: id.legalLast,
            legalFirst: id.legalFirst,
            legalName: id.legalName,
            shortCode: m[1],
            callSlot: m[2] ? Number(m[2]) : null,
            row: r,
            block: bi,
          });
        }
      });
    });
  } catch (err) {
    // The tab is hand-edited; a shape nobody anticipated must cost the rung,
    // not the import.
    return { ...empty, trainees: [], reason: `unreadable: ${err?.message || "unknown error"}`, blocks };
  }

  if (!people.length && !codes.length) {
    return { ...empty, trainees, reason: "no identity/display column pair found", blocks };
  }

  const conflicts = displayConflicts(people);
  return {
    ok: true,
    reason: "",
    source: QGENDA_TAB_TITLE,
    people,
    codes,
    trainees,
    conflicts,
    blocks,
  };
}

/**
 * Are these two legal names the SAME human?
 *
 * ⚠️ A SURNAME-ONLY RECORD IS NOT A DIFFERENT PERSON. The stale report block
 * writes compact keys, so the same colleague appears once as `Robert Schneider`
 * and once as `Schneider`. Treating the string difference as a conflict is what
 * a first draft of this file did, and it silently deleted 22 display names —
 * including every one of the phantom-duplicate cases this tab exists to fix.
 * A conflict is a difference in LETTERS the two records both assert.
 */
function sameLegalPerson(a, b) {
  const at = normalizeName(a?.legalName).split(" ").filter(Boolean);
  const bt = normalizeName(b?.legalName).split(" ").filter(Boolean);
  if (!at.length || !bt.length) return false;
  if (at[at.length - 1] !== bt[bt.length - 1]) return false;
  if (at.length < 2 || bt.length < 2) return true; // one side asserts no given name
  // A `weakFirst` given name was lifted off the printed cell, so all it really
  // asserts is the initial the compact key carried.
  if (a.weakFirst || b.weakFirst) return at[0].charAt(0) === bt[0].charAt(0);
  return at[0] === bt[0];
}

/** Group one display name's entries into the distinct people it names. */
function distinctPeople(entries) {
  const groups = [];
  entries.forEach((e) => {
    const hit = groups.find((g) => sameLegalPerson(g[0], e));
    if (hit) hit.push(e);
    else groups.push([e]);
  });
  return groups;
}

/** Display names that name two DIFFERENT legal people. Never resolved. */
function displayConflicts(people) {
  const byKey = new Map();
  people.forEach((p) => {
    const key = normalizeName(p.displayName);
    if (!key) return;
    byKey.set(key, [...(byKey.get(key) || []), p]);
  });
  const out = [];
  byKey.forEach((entries, key) => {
    const groups = distinctPeople(entries);
    if (groups.length > 1) {
      out.push({ displayName: key, legalNames: groups.map((g) => g[0].legalName) });
    }
  });
  return out;
}

/**
 * Build the lookup the identity ladder uses: normalised display name → the
 * legal identity behind it.
 *
 * ⚠️ Later blocks are STALE (the tab keeps an old printed report below the
 * current one), so the FIRST occurrence wins — that is the freshest report.
 * ⚠️ A display name covering two people is dropped from the map entirely and
 * reported instead; picking either one is the failure mode this whole design
 * exists to remove.
 *
 * @param {object|Array} parsed `parseQgendaIdentity(...)` result, or its people
 * @returns {Map<string, {displayName:string, legalName:string, legalLast:string, legalFirst:string}>}
 */
export function qgendaDisplayIndex(parsed) {
  const people = Array.isArray(parsed) ? parsed : (parsed?.people || []);
  const byKey = new Map();
  people.forEach((p) => {
    const key = normalizeName(p.displayName);
    if (!key || !p.legalName) return;
    byKey.set(key, [...(byKey.get(key) || []), p]);
  });

  const map = new Map();
  byKey.forEach((entries, key) => {
    const groups = distinctPeople(entries);
    if (groups.length !== 1) return; // two people, one printed name → refuse
    // Freshest report first (parse order), and the record that actually
    // asserts a given name in preference to a surname-only compact key.
    const best = groups[0].find((e) => e.legalFirst && !e.weakFirst)
      || groups[0].find((e) => e.legalFirst)
      || groups[0][0];
    map.set(key, {
      displayName: best.displayName,
      legalName: best.legalName,
      legalLast: best.legalLast,
      legalFirst: best.legalFirst,
    });
  });
  return map;
}

/**
 * normalised legal name → MD short code. Freshest report wins — which is also
 * why `callSlot` is carried but is never a property of the doctor: the number
 * is the DAY'S slot index (`MAR (7)` today, `MAR (11)` in February).
 * Also keyed by bare surname, but ONLY where that surname carries exactly one
 * code — the stale block writes compact keys, so `Hawk` and `Thomas Hawk` are
 * the same doctor, while a surname two doctors share resolves to neither.
 */
export function qgendaShortCodeIndex(parsed) {
  const list = Array.isArray(parsed) ? parsed : (parsed?.codes || []);
  const map = new Map();
  const bySurname = new Map();
  list.forEach((c) => {
    const key = normalizeName(c.legalName);
    if (!key) return;
    const entry = { shortCode: c.shortCode, callSlot: c.callSlot, legalName: c.legalName };
    if (!map.has(key)) map.set(key, entry);
    const surname = normalizeName(c.legalLast);
    if (!surname) return;
    const seen = bySurname.get(surname) || { codes: new Set(), entry };
    seen.codes.add(c.shortCode);
    bySurname.set(surname, seen);
  });
  bySurname.forEach((seen, surname) => {
    if (seen.codes.size === 1 && !map.has(surname)) map.set(surname, seen.entry);
  });
  return map;
}

const HOUR_RE = /(\d{1,2})([ap])\s*-\s*(\d{1,2})([ap])/i;
const to24 = (h, ap) => {
  const n = Number(h) % 12;
  return ap.toLowerCase() === "p" ? n + 12 : n;
};

/**
 * Decode a QGenda block header into the window it means.
 *
 *   `AT-APP-7a-3p, (14 M-Th/10 F)` → { startHour: 7, endHour: 15 }
 *   `AT-APP-7p-7a OB`              → { startHour: 19, endHour: 7, overnight: true }
 *
 * ⚠️ THIS IS THE EVIDENCE FOR THE BARE NUMBER. The generator prints a bare
 * number ONLY for a block that starts at 7a, and the number is that block's
 * p.m. end hour — `3` = 7a-3p, `7` = 7a-7p, `24` = 7a-7a. Which is exactly why
 * `7a-7p` and `7p-7a` cannot be told apart once flattened to "7", and why a
 * night CRNA's departure has been computed twelve hours early. A block that
 * does NOT start at 7a makes the generator print the raw window instead —
 * where `7p-7a`, `11a -7a` and `9a-5p` on the sheet come from.
 *
 * Returns null when the header carries no window (`AT-APP-MISC. Shift`).
 */
export function shiftBlockWindow(header) {
  const m = HOUR_RE.exec(String(header || ""));
  if (!m) return null;
  const startHour = to24(m[1], m[2]);
  const endHour = to24(m[3], m[4]);
  return { startHour, endHour, overnight: endHour <= startHour };
}
